import React, { useEffect, useState } from 'react';
import { api, useLanguage } from '../context/AppContext';
import { Zap, ShieldCheck, Target, Trophy, Flame, Loader2 } from 'lucide-react';

/**
 * GamificationPanel — renders the 4 new "armes" live:
 *  1. Perfect Day progress (meals/workout criteria + active multiplier)
 *  2. Team Mission gauge (80% workout)
 *  3. Weekly Boost banner (admin-driven)
 *  4. Relative Leaderboard (top 3 + neighbors)
 *
 * Self-contained & stateless — safe to drop anywhere.
 */
export default function GamificationPanel() {
  const { language } = useLanguage();
  const fr = language === 'fr';

  const [perfectDay, setPerfectDay] = useState(null);
  const [teamMissions, setTeamMissions] = useState([]);
  const [weeklyBoost, setWeeklyBoost] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const loadAll = async () => {
      try {
        const [pd, tm, wb, lb] = await Promise.all([
          api.get('/gamification/perfect-day/status').catch(() => ({ data: null })),
          api.get('/gamification/team-missions/active').catch(() => ({ data: { missions: [] } })),
          api.get('/gamification/weekly-boost/active').catch(() => ({ data: { boost: null } })),
          api.get('/leaderboard/relative?period=week').catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        setPerfectDay(pd.data);
        setTeamMissions(tm.data?.missions || []);
        setWeeklyBoost(wb.data?.boost || null);
        setLeaderboard(lb.data || null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadAll();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="py-4 flex justify-center" data-testid="gamification-loading">
        <Loader2 className="w-5 h-5 animate-spin text-primary/60" />
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="gamification-panel">

      {/* 1. WEEKLY BOOST BANNER (if active) */}
      {weeklyBoost && (
        <div
          className="flex items-center gap-3 p-4 rounded-xl bg-gradient-to-r from-orange-500/20 to-orange-600/10 border border-orange-500/40 shadow-lg shadow-orange-500/10"
          data-testid="weekly-boost-banner"
        >
          <div className="flex-shrink-0 w-11 h-11 rounded-lg bg-orange-500/20 flex items-center justify-center">
            <Target className="w-6 h-6 text-orange-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-orange-300 uppercase tracking-wider leading-tight">
              {fr ? weeklyBoost.label_fr : weeklyBoost.label_en}
            </p>
            <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">
              {fr ? 'Objectif de la semaine — maximise tes points sur cette action !' : 'Weekly objective — maximize your points on this action!'}
            </p>
          </div>
        </div>
      )}

      {/* 2. PERFECT DAY (criteria progress + active multiplier) */}
      {perfectDay && (
        <div
          className={`flex items-center gap-3 p-4 rounded-xl border ${
            perfectDay.active_multiplier > 1.0
              ? 'bg-gradient-to-r from-yellow-500/20 to-yellow-600/10 border-yellow-500/40 shadow-lg shadow-yellow-500/10'
              : 'bg-secondary/40 border-border'
          }`}
          data-testid="perfect-day-card"
        >
          <div className="flex-shrink-0 w-11 h-11 rounded-lg bg-yellow-500/20 flex items-center justify-center">
            {perfectDay.active_multiplier > 1.0
              ? <Flame className="w-6 h-6 text-yellow-400 animate-pulse" />
              : <Zap className="w-6 h-6 text-yellow-400" />
            }
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-yellow-300 uppercase tracking-wider leading-tight">
              {perfectDay.active_multiplier > 1.0
                ? (fr ? `🔥 Multiplicateur ×${perfectDay.active_multiplier.toFixed(1)} ACTIF` : `🔥 Multiplier ×${perfectDay.active_multiplier.toFixed(1)} ACTIVE`)
                : (fr ? 'Journée Parfaite' : 'Perfect Day')}
            </p>
            <div className="flex items-center gap-3 mt-1 text-[11px] text-muted-foreground">
              <span data-testid="perfect-day-meals">
                🍽️ {perfectDay.today.criteria_met.meals_logged}/{perfectDay.today.criteria_met.meals_required}
              </span>
              <span data-testid="perfect-day-workouts">
                💪 {perfectDay.today.criteria_met.workouts_logged}/{perfectDay.today.criteria_met.workouts_required}
              </span>
              {perfectDay.today.is_perfect && (
                <span className="text-yellow-400 font-semibold">
                  {fr ? '✅ Gagne ×1.2 demain !' : '✅ Earn ×1.2 tomorrow!'}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 3. TEAM MISSIONS */}
      {teamMissions.map(m => {
        const pct = Math.min(100, m.current_percent || 0);
        const achieved = m.status === 'achieved';
        return (
          <div
            key={m.mission_id}
            className={`p-4 rounded-xl border ${
              achieved
                ? 'bg-indigo-500/20 border-indigo-400/60'
                : 'bg-indigo-500/10 border-indigo-500/30'
            }`}
            data-testid={`team-mission-${m.mission_id}`}
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="flex-shrink-0 w-11 h-11 rounded-lg bg-indigo-500/20 flex items-center justify-center">
                <ShieldCheck className={`w-6 h-6 text-indigo-300 ${achieved ? 'animate-pulse' : ''}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-indigo-300 uppercase tracking-wider leading-tight">
                  {fr ? 'Force de Frappe' : 'Strike Force'}
                </p>
                <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">
                  {fr ? m.label_fr : m.label_en}
                </p>
              </div>
            </div>
            {/* Progress bar */}
            <div className="relative w-full h-2.5 bg-border rounded-full overflow-hidden">
              <div
                className={`absolute inset-y-0 left-0 transition-all duration-700 ease-out ${
                  achieved ? 'bg-green-500' : 'bg-indigo-400'
                }`}
                style={{ width: `${pct}%` }}
                data-testid={`team-mission-bar-${m.mission_id}`}
              />
            </div>
            <div className="flex justify-between items-center mt-1.5 text-[11px]">
              <span className="text-muted-foreground">
                {m.members_completed}/{m.total_active_members} {fr ? 'soldats' : 'soldiers'}
              </span>
              <span className={`font-mono font-semibold ${achieved ? 'text-green-400' : 'text-indigo-300'}`}>
                {pct}% / {m.target_percent}%
              </span>
            </div>
            {achieved && (
              <p className="mt-2 text-xs text-green-400 font-semibold text-center">
                {fr ? `⚔️ Mission accomplie! +${m.reward.value} pts distribués à tout le peloton` : `⚔️ Mission complete! +${m.reward.value} pts distributed to all platoon`}
              </p>
            )}
          </div>
        );
      })}

      {/* 4. RELATIVE LEADERBOARD */}
      {leaderboard && leaderboard.total_players > 0 && (
        <div className="p-4 rounded-xl bg-secondary/40 border border-border" data-testid="relative-leaderboard">
          <div className="flex items-center gap-2 mb-3">
            <Trophy className="w-4 h-4 text-primary" />
            <p className="text-sm font-bold uppercase tracking-wider">
              {fr ? 'Classement Tactique' : 'Tactical Ranking'}
            </p>
            <span className="ml-auto text-[10px] text-muted-foreground">
              {leaderboard.total_players.toLocaleString()} {fr ? 'soldats' : 'soldiers'}
            </span>
          </div>
          <div className="space-y-1.5">
            {leaderboard.top.map(row => (
              <LeaderboardRow key={`t-${row.rank}`} row={row} />
            ))}
            {leaderboard.user_rank && leaderboard.user_rank > (leaderboard.top.length || 0) && (
              <>
                <div className="text-center text-muted-foreground text-xs py-0.5">⋯</div>
                {leaderboard.neighbors.map(row => (
                  <LeaderboardRow key={`n-${row.rank}`} row={row} />
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const LeaderboardRow = ({ row }) => {
  const rankColor =
    row.rank === 1 ? 'text-yellow-400'
    : row.rank === 2 ? 'text-gray-300'
    : row.rank === 3 ? 'text-amber-600'
    : 'text-muted-foreground';

  return (
    <div
      className={`flex items-center gap-2 px-2 py-1.5 rounded-lg ${
        row.is_me ? 'bg-primary/15 border border-primary/40 ring-1 ring-primary/30' : ''
      }`}
      data-testid={`leaderboard-row-${row.rank}`}
    >
      <span className={`font-mono font-bold text-xs w-10 flex-shrink-0 ${rankColor}`}>
        #{row.rank}
      </span>
      <span className={`text-xs flex-1 truncate ${row.is_me ? 'font-bold text-primary' : 'text-foreground'}`}>
        {row.name}
      </span>
      <span className="text-xs font-mono text-muted-foreground">
        {row.points_total} pts
      </span>
    </div>
  );
};
